/**
 * Whether a member must verify a phone number before using the app (ELARA-1170).
 *
 * A verified number is what lets a member message Elara by SMS, so every account needs one. Onboarding
 * will ask for it; members who joined before that are held by a blocking prompt once their account is
 * more than {@link PHONE_REQUIRED_AFTER_MS} old: a page on web (`VerifyPhonePage`) and a full-screen
 * gate on mobile (`PhoneRequiredGate`). Both read {@link isRequired}, so the two platforms cannot
 * disagree about who is held.
 *
 * Staff are never held: anyone who can open the admin console (privileged, or holding an admin role).
 * Their accounts are for running the service, not for being contacted about a booking. Roles arrive
 * with the custom claims, a moment after sign-in, so the prompt waits for them; otherwise a member of
 * staff would see it flash on every launch.
 *
 * The prompt waits for the other app-wide gates. An unaccepted policy update or a locked membership
 * holds the member first, so two blocking prompts never stack. It also waits for onboarding, which runs
 * before any of them.
 *
 * Client-side only: the server does not refuse members without a number. This is the ask, not the
 * enforcement.
 *
 * `isRequired` is a computed signal, so the account age is checked when the app loads and whenever
 * the member, their phone or their profile changes, not on a timer. A member who crosses the day mark
 * with the app open is held on their next launch.
 */

import type { MembershipPaymentState } from './MembershipPaymentState.js';
import type { PhoneLinkState } from './PhoneLinkState.js';
import type { PolicyAcceptanceState } from './PolicyAcceptanceState.js';
import type { SessionState } from './SessionState.js';
import type { UserProfileState } from './UserProfileState.js';

import { createState } from '#lib/signals-utils.js';

/** How long after signing up a member may use the app without a verified phone. */
export const PHONE_REQUIRED_AFTER_MS = 24 * 60 * 60 * 1000;

export interface PhoneRequirementDeps {
  phoneLinkState: Pick<PhoneLinkState, 'isVerified'>;
  sessionState: Pick<SessionState, 'user' | 'isLoading' | 'customClaims' | 'canAccessAdmin'>;
  userProfileState: Pick<UserProfileState, 'isLoading' | 'profile'>;
  policyAcceptanceState: Pick<PolicyAcceptanceState, 'requiresAcceptance'>;
  membershipPaymentState: Pick<MembershipPaymentState, 'isLocked'>;
  now?: () => number;
}

export function createPhoneRequirementState({
  phoneLinkState,
  sessionState,
  userProfileState,
  policyAcceptanceState,
  membershipPaymentState,
  now = Date.now,
}: PhoneRequirementDeps) {
  return createState({
    /** Whether to hold the member until they verify a phone number. */
    get isRequired(): boolean {
      const user = sessionState.user;
      if (!user || sessionState.isLoading || phoneLinkState.isVerified) return false;
      // Undefined until the claims load, and if they fail to: not holding then is the safe side.
      if (!sessionState.customClaims || sessionState.canAccessAdmin) return false;
      // Undefined while loading, which is not "no profile": holding on that would flash the prompt.
      if (userProfileState.isLoading || !userProfileState.profile?.onboardingCompletedAt) return false;
      if (policyAcceptanceState.requiresAcceptance || membershipPaymentState.isLocked) return false;
      // An unreadable sign-up time lets the member in rather than holding them on a guess.
      const signedUpAt = Date.parse(user.metadata.creationTime ?? '');
      return Number.isFinite(signedUpAt) && now() - signedUpAt > PHONE_REQUIRED_AFTER_MS;
    },
  });
}

export type PhoneRequirementState = ReturnType<typeof createPhoneRequirementState>;
